const API_BASE_URL = process.env.REACT_APP_API_URL || '';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // ── Token helpers ─────────────────────────────────────────
  getAccess()  { return localStorage.getItem('accessToken'); }
  getRefresh() { return localStorage.getItem('refreshToken'); }

  storeTokens(access, refresh) {
    localStorage.setItem('accessToken', access);
    if (refresh) localStorage.setItem('refreshToken', refresh);
  }

  clearTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  // ── Refresh access token ──────────────────────────────────
  async refreshAccessToken() {
    const refreshToken = this.getRefresh();
    if (!refreshToken) return null;
    try {
      const res  = await fetch(`${this.baseURL}/auth/refresh`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      this.storeTokens(data.accessToken, data.refreshToken);
      return data.accessToken;
    } catch {
      return null;
    }
  }

  // ── Core request with auto token refresh ──────────────────
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    const makeRequest = (token) => {
      const headers = { 'Content-Type': 'application/json', ...options.headers };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      return fetch(url, { ...options, headers });
    };

    let res = await makeRequest(this.getAccess());

    // Token expired — try refresh once
    if (res.status === 401) {
      const newToken = await this.refreshAccessToken();
      if (newToken) {
        res = await makeRequest(newToken);
      } else {
        // Refresh failed — force logout
        this.clearTokens();
        window.location.href = '/login';
        return;
      }
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      throw new Error(err.error || err.message || `HTTP ${res.status}`);
    }

    return res.json();
  }

  // ── Auth ──────────────────────────────────────────────────
  async login(email, password, businessId) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, businessId }),
    });
  }

  async activate(email, businessId, tempCode, newPassword) {
    return this.request('/auth/activate', {
      method: 'POST',
      body: JSON.stringify({ email, businessId, tempCode, newPassword }),
    });
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async invite(email, firstName, lastName, role, teamId) {
    return this.request('/auth/invite', {
      method: 'POST',
      body: JSON.stringify({ email, firstName, lastName, role, teamId }),
    });
  }

  // ── Business Data ─────────────────────────────────────────
  async getBusinessData() { return this.request('/api/business-data'); }
  async updateBusinessData(data) {
    return this.request('/api/business-data', { method: 'PUT', body: JSON.stringify(data) });
  }

  // ── Employees ─────────────────────────────────────────────
  async getEmployees() { return this.request('/api/employees'); }
  async createEmployee(data) {
    return this.request('/api/employees', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateEmployee(id, data) {
    return this.request(`/api/employees/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteEmployee(id) {
    return this.request(`/api/employees/${id}`, { method: 'DELETE' });
  }

  // ── Chat ──────────────────────────────────────────────────
  async sendChatMessage(message) {
    return this.request('/api/chat', { method: 'POST', body: JSON.stringify({ message }) });
  }
  async getChatHistory() { return this.request('/api/chat/history'); }

  // ── Modules ───────────────────────────────────────────────
  async getModules() { return this.request('/api/modules'); }
  async createModule(data) {
    return this.request('/api/modules', { method: 'POST', body: JSON.stringify(data) });
  }
  async deleteModule(id) { return this.request(`/api/modules/${id}`, { method: 'DELETE' }); }

  // ── File Upload ───────────────────────────────────────────
  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request('/api/upload', { method: 'POST', headers: {}, body: formData });
  }

  // ── Analytics ─────────────────────────────────────────────
  async getAnalytics()        { return this.request('/api/analytics'); }
  async getPerformanceMetrics() { return this.request('/api/metrics'); }
}

// ─── Global fetch interceptor — attaches JWT to all raw fetch calls ──────────
const _origFetch = window.fetch.bind(window);
window.fetch = async (url, options = {}) => {
  const token = localStorage.getItem('accessToken');
  const isExternal = typeof url === 'string' && (
    url.startsWith('http') && !url.includes('localhost:3000')
  );
  if (token && !isExternal) {
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };
  }
  return _origFetch(url, options);
};
// ─────────────────────────────────────────────────────────────────────────────
export default new ApiService();
